import type { Id } from "@convex/_generated/dataModel";

interface SubmitFeedbackArgs {
  title?: string;
  type: "bug" | "enhancement";
  description: string;
  email?: string;
  pageUrl?: string;
  errorDigest?: string;
  diagnostics?: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  screenshotStorageIds?: Array<Id<"_storage">>;
  errorEventId?: string;
  posthogReplayUrl?: string;
  posthogSessionId?: string;
  userAgent?: string;
  submissionKey: string;
  createGitHubIssue: (args: {
    title: string;
    type: "bug" | "enhancement";
    description: string;
    stepsToReproduce?: string;
    expectedBehavior?: string;
    pageUrl?: string;
    contactEmail?: string;
    screenshotStorageIds?: Array<Id<"_storage">>;
    errorOutput?: string;
    errorEventId?: string;
    posthogReplayUrl?: string;
    posthogSessionId?: string;
    userAgent?: string;
    submissionKey?: string;
  }) => Promise<{
    reportId: Id<"issueReports">;
    issueNumber?: number;
    issueUrl?: string;
  }>;
}

interface SubmitFeedbackResult {
  convexOk: boolean;
  issueUrl?: string;
}

export async function submitFeedback(
  args: SubmitFeedbackArgs,
): Promise<SubmitFeedbackResult> {
  const resolvedTitle =
    args.title || `${args.type === "bug" ? "Bug report" : "Feature request"}`;

  const errorOutput =
    args.diagnostics ??
    (args.errorDigest ? `Error digest: ${args.errorDigest}` : undefined);

  const convexPromise = args.createGitHubIssue({
    title: resolvedTitle,
    type: args.type,
    description: args.description,
    stepsToReproduce: args.stepsToReproduce,
    expectedBehavior: args.expectedBehavior,
    pageUrl: args.pageUrl,
    contactEmail: args.email,
    screenshotStorageIds: args.screenshotStorageIds,
    errorOutput,
    errorEventId: args.errorEventId,
    posthogReplayUrl: args.posthogReplayUrl,
    posthogSessionId: args.posthogSessionId,
    userAgent: args.userAgent,
    submissionKey: args.submissionKey,
  });

  const [convexResult] = await Promise.allSettled([convexPromise]);
  const convexOk = convexResult.status === "fulfilled";
  const issueUrl =
    convexResult.status === "fulfilled"
      ? convexResult.value.issueUrl
      : undefined;

  return { convexOk, issueUrl };
}
